import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FaCarSide } from "react-icons/fa";
import { apiClient } from "../services/apiClient";
import { fleetService, vehicleImageUrl } from "../services/fleetService";
import { useI18n } from "../providers/I18nProvider";
import { normalizeRoleName } from "../utils/roles";
import { useConfirm } from "../providers/ConfirmProvider";
import NoirAlert from "../components/NoirAlert";
import { TabBar, FilterBar, SearchInput, EmptyState } from "../components/ListToolbar";
import "../styles/Fleet.css";

const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];

const AdminVehiclesPage = () => {
  const { t } = useI18n();
  const confirm = useConfirm();
  const [notification, setNotification] = useState({ message: "", type: "" });
  const clearNotification = useCallback(() => setNotification({ message: "", type: "" }), []);
  const notifyError = (err) =>
    setNotification({ message: err.response?.data?.msg || t("fleet.errors.generic"), type: "error" });
  const [tab, setTab] = useState("active");
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("name");
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [plate, setPlate] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [requireApproval, setRequireApproval] = useState(false);
  const [saving, setSaving] = useState(false);
  const [roleName, setRoleName] = useState("");
  const fileInputRef = useRef(null);
  const canManageFleet = roleName === "admin";

  const loadVehicles = () => {
    setLoading(true);
    setError("");
    apiClient
      .get("/fleet/vehicles", { params: { active: "false" } })
      .then((r) => setVehicles(r.data))
      .catch((err) =>
        setError(err.response?.data?.msg || t("fleet.loadError"))
      )
      .finally(() => setLoading(false));
  };

  const loadSettings = () => {
    apiClient
      .get("/site-settings")
      .then((r) => {
        setRequireApproval(
          Boolean(r.data?.vehicle_reservations_require_approval)
        );
      })
      .catch(() => {});
  };

  useEffect(() => {
    loadVehicles();
    loadSettings();
    apiClient
      .get("/users/me")
      .then((r) => setRoleName(normalizeRoleName(r.data?.role_name)))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleRequireApproval = async () => {
    const next = !requireApproval;
    setRequireApproval(next);
    try {
      await apiClient.put(
        "/site-settings/vehicle_reservations_require_approval",
        { value: next }
      );
    } catch (err) {
      setRequireApproval(!next);
      notifyError(err);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setName("");
    setPlate("");
    setImageFile(null);
    setModalOpen(true);
  };

  const openEdit = (v) => {
    setEditing(v);
    setName(v.name);
    setPlate(v.plate || "");
    setImageFile(null);
    setModalOpen(true);
  };

  const onPickImage = (e) => {
    const file = e.target.files?.[0] || null;
    if (file && !IMAGE_TYPES.includes(file.type)) {
      setNotification({ message: t("fleetUi.imageBadType"), type: "error" });
      e.target.value = "";
      setImageFile(null);
      return;
    }
    if (file && file.size > MAX_IMAGE_BYTES) {
      setNotification({ message: t("fleetUi.imageTooLarge"), type: "error" });
      e.target.value = "";
      setImageFile(null);
      return;
    }
    setImageFile(file);
  };

  const onSave = async (e) => {
    e.preventDefault();
    if (saving || !name.trim()) return;
    setSaving(true);
    try {
      let vehicle;
      if (editing) {
        vehicle = await fleetService.updateVehicle(editing.id, {
          name: name.trim(),
          plate: plate.trim() || null,
        });
      } else {
        vehicle = await fleetService.createVehicle({
          name: name.trim(),
          plate: plate.trim() || null,
        });
      }
      if (imageFile) {
        await fleetService.uploadVehicleImage(vehicle.id, imageFile);
      }
      setModalOpen(false);
      setNotification({ message: t("fleetUi.saved"), type: "success" });
      loadVehicles();
    } catch (err) {
      notifyError(err);
    } finally {
      setSaving(false);
    }
  };

  const onToggleActive = async (v) => {
    if (v.is_active && !(await confirm(`"${v.name}" — ${t("fleet.deactivate")}?`))) return;
    try {
      if (v.is_active) {
        await fleetService.deleteVehicle(v.id);
      } else {
        await fleetService.updateVehicle(v.id, { is_active: true });
      }
      loadVehicles();
    } catch (err) {
      notifyError(err);
    }
  };

  // Modal: Esc ile kapanır
  useEffect(() => {
    if (!modalOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !saving) setModalOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [modalOpen, saving]);

  const counts = useMemo(
    () => ({
      active: vehicles.filter((v) => v.is_active).length,
      inactive: vehicles.filter((v) => !v.is_active).length,
      all: vehicles.length,
    }),
    [vehicles]
  );

  const visible = useMemo(() => {
    const q = search.trim().toLocaleLowerCase("tr-TR");
    const key = sortKey === "plate" ? "plate" : "name";
    return vehicles
      .filter((v) => (tab === "active" ? v.is_active : tab === "inactive" ? !v.is_active : true))
      .filter((v) => !q || `${v.name || ""} ${v.plate || ""}`.toLocaleLowerCase("tr-TR").includes(q))
      .sort((a, b) => String(a[key] || "").localeCompare(String(b[key] || ""), "tr"));
  }, [vehicles, tab, search, sortKey]);

  return (
    <div className="fleet-page">
      <NoirAlert message={notification.message} type={notification.type} onClose={clearNotification} />
      <div className="fleet-page-header">
        <div>
          <h2>{t("fleet.adminVehiclesTitle")}</h2>
          <div className="subtitle">{t("fleet.adminVehiclesSubtitle")}</div>
        </div>
      </div>

      {!canManageFleet && (
        <div className="fleet-error" style={{ marginBottom: 12 }}>
          {t("fleet.adminOnlyNote")}
        </div>
      )}

      <div className="fleet-admin-toolbar">
        {canManageFleet && (
          <>
            <button className="fleet-btn" onClick={openCreate} type="button">
              + {t("fleet.addVehicle")}
            </button>
            <label className="fleet-admin-toggle">
              <input
                type="checkbox"
                checked={requireApproval}
                onChange={toggleRequireApproval}
              />
              <span>{t("fleet.requireApproval")}</span>
            </label>
          </>
        )}
      </div>

      {loading && <div className="fleet-empty">{t("common.loading")}</div>}
      {error && (
        <div className="fleet-error" role="alert">
          {error}
        </div>
      )}

      {!loading && !error && (
        <>
          <TabBar
            tabs={[
              { key: "active", label: t("fleetUi.tabs.active"), count: counts.active },
              { key: "inactive", label: t("fleetUi.tabs.inactive"), count: counts.inactive },
              { key: "all", label: t("fleetUi.tabs.all"), count: counts.all },
            ]}
            value={tab}
            onChange={setTab}
          />
          <FilterBar showReset={Boolean(search)} onReset={() => setSearch("")}>
            <SearchInput value={search} onChange={setSearch} placeholder={t("fleetUi.filters.searchVehicle")} />
            <select
              className="leaves-filter-input lt-input"
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value)}
              aria-label={t("fleetUi.filters.sort")}
            >
              <option value="name">{t("fleetUi.filters.sortNameAsc")}</option>
              <option value="plate">{t("fleetUi.filters.sortPlateAsc")}</option>
            </select>
          </FilterBar>
          {visible.length === 0 && (
            <EmptyState
              message={vehicles.length === 0 ? t("fleet.noVehicles") : t("fleetUi.emptyVehiclesFiltered")}
            />
          )}
        </>
      )}

      <div className="vehicle-grid">
        {visible.map((v) => {
          const imgUrl = vehicleImageUrl(v.image_path);
          return (
            <div key={v.id} className="vehicle-card" style={{ cursor: "default" }}>
              <div
                className="vehicle-card-image"
                style={imgUrl ? { backgroundImage: `url(${imgUrl})` } : undefined}
              >
                {!imgUrl && (
                  <div className="vehicle-card-placeholder">
                    <FaCarSide />
                  </div>
                )}
              </div>
              <div className="vehicle-card-body">
                <h3 className="vehicle-card-title">{v.name}</h3>
                {v.plate && <div className="vehicle-card-plate">{v.plate}</div>}
                <span
                  className={`vehicle-card-status ${
                    v.is_active ? "" : "inactive"
                  }`}
                >
                  {v.is_active ? t("fleet.active") : t("fleet.inactive")}
                </span>
                {canManageFleet && (
                  <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
                    <button
                      className="fleet-btn secondary"
                      type="button"
                      onClick={() => openEdit(v)}
                    >
                      {t("common.edit")}
                    </button>
                    <button
                      className={v.is_active ? "fleet-btn danger" : "fleet-btn success"}
                      type="button"
                      onClick={() => onToggleActive(v)}
                    >
                      {v.is_active ? t("fleet.deactivate") : t("fleet.activate")}
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {modalOpen && (
        <div
          className="fleet-modal-backdrop"
          onMouseDown={(e) => e.target === e.currentTarget && !saving && setModalOpen(false)}
        >
          <div className="fleet-modal" role="dialog" aria-modal="true">
            <h3>{editing ? t("fleet.editVehicle") : t("fleet.addVehicle")}</h3>
            <form onSubmit={onSave}>
              <div className="fleet-field">
                <label>{t("fleet.name")}</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoFocus
                  required
                />
              </div>
              <div className="fleet-field">
                <label>{t("fleet.plate")}</label>
                <input
                  type="text"
                  value={plate}
                  onChange={(e) => setPlate(e.target.value)}
                  placeholder="06 ABC 123"
                />
              </div>
              <div className="fleet-field">
                <label>{t("fleet.image")}</label>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  ref={fileInputRef}
                  onChange={onPickImage}
                />
                <small style={{ opacity: 0.7 }}>
                  {t("fleet.imageHint")}
                </small>
              </div>
              <div className="fleet-modal-actions">
                <button
                  type="button"
                  className="fleet-btn secondary"
                  onClick={() => setModalOpen(false)}
                >
                  {t("common.cancel")}
                </button>
                <button type="submit" className="fleet-btn" disabled={saving}>
                  {saving ? t("common.loading") : t("common.save")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminVehiclesPage;
